import { ReactElement, useMemo } from 'react';

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro';

import { auditLogsCols } from '@/components/AuditLogs/AuditLogsCols';
import {
  createColumns,
  DataGridColSettings,
} from '@/components/DataGrid/utils';
import { addressNoteCols } from '@/components/Modals/AddressNotesModal/AddressNoteColumns';
import { cautionCols } from '@/components/Modals/CautionModal/CautionCols';
import { cfsHistoryCols } from '@/components/Modals/CFSHistoryModal/CFSHistoryColumns';
import { kasePropertyCols } from '@/features/properties/propertyCols';
import { masterNamesCols } from '@/layouts/TabLayout/MasterNames/MasterNameCols';
import { masterNameNoteCols } from '@/layouts/TabLayout/MasterNames/MasterNameNotesCols';
import { sharedVehicleCols } from '@/layouts/TabLayout/MasterVehicles/SharedVehicleCols';
import {
  IMasterNameRelationBehavior,
  IOffenseOption,
  IUser,
  MasterNameInvolvement,
} from '@/models';
import { callRecordsCols } from '@/pages/CallRecords/CallRecordsCols';
import { cfsRelatedRecordsCols } from '@/pages/CFS/CFSRelatedRecords/CFSRelatedRecordsCols';
import { cfsCols } from '@/pages/CFS/List/CFSCols';
import { agencyEmployeeCols } from '@/pages/Employees/AgencyEmployeeCols';
import { employeeDevicesCols } from '@/pages/Employees/EmployeeDevicesCols';
import { agencyEquipmentCols } from '@/pages/Equipments/AgencyEquipmentCols';
import { agencyInventoryLocationCols } from '@/pages/InventoryLocations/AgencyInventoryLocationsCols';
import { bookingCols } from '@/pages/JMS/Managament/components/grids/columns/bookingCols';
import { bookingHistoryCols } from '@/pages/JMS/Managament/components/grids/columns/bookingHistoryCol';
import { inmateCols } from '@/pages/JMS/Managament/components/grids/columns/inmateCols';
import { inmateNoteCols } from '@/pages/JMS/Managament/components/grids/columns/inmateNoteCols';
import { importedKaseCols } from '@/pages/Kases/ImportedKaseList/ImportedKaseCols';
import { kaseArrestCols } from '@/pages/Kases/KaseArrests/KaseArrestCols';
import { kaseCols } from '@/pages/Kases/KaseList/KaseCols';
import { masterAddressCols } from '@/pages/MasterIndex/Address/MasterAddressCols';
import {
  personCfsCols,
  personArrestCols,
  personCaseCols,
  personWarrantCols,
  personTaskCols,
  personDirCols,
} from '@/pages/MasterIndex/components/PersonRelatedRecordsCols';
import { relatedRecords } from '@/pages/MasterIndex/components/RelatedRecordsCols';
import { wantedPersonRecords } from '@/pages/MasterIndex/components/WantedPersonRecordsCols';
import { masterLogsCols } from '@/pages/MasterIndex/Logs/MasterLogsCols';
import { masterOrganizationCols } from '@/pages/MasterIndex/Organizations/MasterOrganizationsCols';
import { masterPersonsCols } from '@/pages/MasterIndex/Persons/MasterPersonsCols';
import { masterPropertyCols } from '@/pages/MasterIndex/Properties/MasterPropertiesCols';
import { masterVehicleCols } from '@/pages/MasterIndex/Vehicles/MasterVehiclesCols';
import { ncicSearchRequestsCols } from '@/pages/NCIC/NCICSearchRequestsCols';
import { propertyEvidenceCols } from '@/pages/Properties/PropertyEvidenceCols';
import { taskCols } from '@/pages/Tasks/TaskCols';
import { arrestsToolCols } from '@/pages/Tools/Arrests/ArrestsToolCols';
import { buildingInspectionRecordsCols } from '@/pages/Tools/BuildingInspection/components/BuildingInspectionRecordsCols';
import { inspectionFeesCols } from '@/pages/Tools/BuildingInspection/components/FeesDataGridCols';
import { inspectionViolationsCols } from '@/pages/Tools/BuildingInspection/components/ViolationsDataGridCols';
import { inspectionVisitNotes } from '@/pages/Tools/BuildingInspection/components/VisitNotesDataGridCols';
import { domesticIncidentReportCols } from '@/pages/Tools/DomesticIncidentReport/Tables/DomesticIncidentReportCols';
import { InternalAffairsAuditTrailsCols } from '@/pages/Tools/InternalAffairs/Tables/InternalAffairsAuditTrailCols';
import { internalAffairsCols } from '@/pages/Tools/InternalAffairs/Tables/InternalAffairsCols';
import { InternalAffairsPeopleInvolvedCols } from '@/pages/Tools/InternalAffairs/Tables/InternalAffairsPeopleInvolvedCols';
import { InternalAffairsRelatedRecordsCols } from '@/pages/Tools/InternalAffairs/Tables/InternalAffairsRelatedRecordsCols';
import { sexOffenderCols } from '@/pages/Tools/SexOffenders/SexOffenderCols';
import { towingCompaniesCols } from '@/pages/Tools/TowingManagement/Tables/TowingCompanyCols';
import { towingRecords } from '@/pages/Tools/TowingManagement/Tables/TowingRecordsCols';
import { vehicleListCols } from '@/pages/Tools/VehicleMaintenance/VehicleCols';
import { vehicleMaintenanceCols } from '@/pages/Tools/VehicleMaintenance/VehicleMaintenanceCols';
import { wantedpersonCols } from '@/pages/Tools/WantedPerson/WantedPersonCols';
import { agencyUnitCols } from '@/pages/Units/AgencyUnitCols';
import { unitShiftsCols } from '@/pages/UnitShifts/UnitShiftsCols';
import { unitShiftsHistoryCols } from '@/pages/UnitShifts/UnitShiftsHistoryCols';
import { unitTypeCols } from '@/pages/UnitType/UnitTypeCols';
import { agencyVehicleCols } from '@/pages/Vehicles/AgencyVehicleCols';
import { agencyVendorCols } from '@/pages/Vendors/AgencyVendorCols';

type ColumnGenerator = () => Partial<GridColDef>;

type ColumnMap = Record<string, ColumnGenerator>;

type GridType =
  | 'kase'
  | 'importedKase'
  | 'kaseVehicle'
  | 'kaseArrests'
  | 'kaseProperties'
  | 'cfs'
  | 'cfsHistory'
  | 'cfsRelatedRecords'
  | 'caution'
  | 'task'
  | 'masterNames'
  | 'masterNamesNotes'
  | 'masterPersons'
  | 'masterOrganizations'
  | 'masterVehicles'
  | 'masterProperties'
  | 'masterAddress'
  | 'masterLogs'
  | 'agencyEmployee'
  | 'agencyVehicle'
  | 'agencyEquipment'
  | 'agencyUnit'
  | 'agencyVendor'
  | 'agencyInventoryLocation'
  | 'auditLogs'
  | 'addressNote'
  | 'employeeDevices'
  | 'unitShiftsHistory'
  | 'unitShifts'
  | 'inmate'
  | 'inmateBooking'
  | 'inmateNotes'
  | 'callRecords'
  | 'relatedRecords'
  | 'wantedPersonRecords'
  | 'wantedPerson'
  | 'propertyEvidence'
  | 'ncicSearchRequests'
  | 'inmateBookingHistory'
  | 'unitTypes'
  | 'vehicleListCols'
  | 'vehicleMaintenance'
  | 'towingCompanies'
  | 'towingRecords'
  | 'inspectionFeesCols'
  | 'inspectionViolationsCols'
  | 'inspectionVisitNotes'
  | 'buildingInspectionListCols'
  | 'internalAffairs'
  | 'peopleInvolved'
  | 'relatedRecordsIA'
  | 'auditTrail'
  | 'domesticIncidentReports'
  | 'arrestsTool'
  | 'personCfs'
  | 'personArrests'
  | 'personCases'
  | 'personWarrants'
  | 'personTasks'
  | 'personDir'
  | 'sexOffender';

interface GridConfig {
  type: GridType;
  columnSettings: DataGridColSettings[];
  actionsNode?: (params: GridRenderCellParams) => ReactElement;
  navigate?: (path: string) => void;
  onDelete?: (row: any) => void;
  openMenu?: (event: React.MouseEvent<HTMLButtonElement>, param: any) => void;
  onView?: (row: any) => void;
  onEdit?: (row: any) => void;
  onPrint?: (row: any) => void;
  isReadOnly?: boolean;
  onSetInvolvement?: (
    event: React.MouseEvent<HTMLButtonElement>,
    param: any,
  ) => void;
  onGotoNotes?: (
    event: React.MouseEvent<HTMLButtonElement>,
    param: any,
  ) => void;
  onInvolvementUpdate?: (
    nameId: string,
    involvements: MasterNameInvolvement[],
    behavior: IMasterNameRelationBehavior,
  ) => Promise<void>;
  isKase?: boolean;
  user?: IUser | null;
  isNameFilterActive?: boolean;
  filteredOptions?: IOffenseOption[];
  agencyPropertyTypes?: { value: string; label: string }[];
  hasViewRole?: boolean;
  actionMethods?: any;
  onExport?: (row: any) => void;
  kaseNumber?: string;
  cfsDisplayOption?: string;
}

//TODO: FIX  THIS
export const useDataGridColumns = ({
  type,
  columnSettings,
  actionsNode,
  navigate,
  onDelete,
  openMenu,
  onView,
  onEdit,
  onPrint,
  isReadOnly,
  onSetInvolvement,
  onGotoNotes,
  onInvolvementUpdate,
  isKase,
  isNameFilterActive,
  filteredOptions,
  agencyPropertyTypes,
  hasViewRole,
  actionMethods,
  cfsDisplayOption,
  onExport,
  kaseNumber,
}: GridConfig): GridColDef[] => {
  const gridSpecificMaps: Record<GridType, ColumnMap> = {
    importedKase: importedKaseCols(openMenu),
    kase: kaseCols(openMenu),
    kaseVehicle: sharedVehicleCols(openMenu),
    cfs: cfsCols(actionsNode),
    cfsHistory: cfsHistoryCols(onView),
    cfsRelatedRecords: cfsRelatedRecordsCols(actionMethods?.onUnlink),
    employeeDevices: employeeDevicesCols(),
    caution: cautionCols(onDelete),
    task: taskCols(openMenu),
    masterNames: masterNamesCols({
      navigate,
      onEdit,
      onDelete,
      onSetInvolvement,
      onGotoNotes,
      openMenu,
      isKase,
      isReadOnly,
      onInvolvementUpdate,
    }),
    masterNamesNotes: masterNameNoteCols(openMenu),
    masterPersons: masterPersonsCols(
      navigate,
      onDelete,
      openMenu,
      isNameFilterActive,
    ),
    masterOrganizations: masterOrganizationCols(navigate, onDelete, openMenu),
    masterVehicles: masterVehicleCols(
      navigate,
      onDelete,
      filteredOptions,
      openMenu,
    ),
    masterProperties: masterPropertyCols(navigate, filteredOptions, openMenu),
    masterAddress: masterAddressCols(navigate, onDelete, openMenu),
    masterLogs: masterLogsCols(),
    kaseArrests: kaseArrestCols(
      onView,
      onEdit,
      onPrint,
      isReadOnly,
      filteredOptions,
      onExport,
      kaseNumber,
      actionMethods?.isPrinting,
      actionMethods?.printingArresteeId,
    ),
    kaseProperties: kasePropertyCols(
      openMenu,
      filteredOptions,
      agencyPropertyTypes,
    ),
    agencyEmployee: agencyEmployeeCols(openMenu),
    agencyVehicle: agencyVehicleCols(openMenu),
    agencyEquipment: agencyEquipmentCols(openMenu),
    agencyUnit: agencyUnitCols(openMenu),
    agencyVendor: agencyVendorCols(openMenu),
    agencyInventoryLocation: agencyInventoryLocationCols(openMenu),
    auditLogs: auditLogsCols(),
    addressNote: addressNoteCols(onDelete),
    unitShiftsHistory: unitShiftsHistoryCols(cfsDisplayOption),
    inmate: inmateCols(onView, onEdit, openMenu, navigate),
    inmateBooking: bookingCols(onView, onEdit),
    inmateNotes: inmateNoteCols(),
    inmateBookingHistory: bookingHistoryCols(onView),
    callRecords: callRecordsCols(openMenu),
    unitTypes: unitTypeCols(openMenu),
    unitShifts: unitShiftsCols(),
    relatedRecords: relatedRecords(navigate),
    wantedPersonRecords: wantedPersonRecords(navigate, hasViewRole),
    wantedPerson: wantedpersonCols(openMenu),
    vehicleListCols: vehicleListCols(openMenu),
    vehicleMaintenance: vehicleMaintenanceCols(openMenu),
    propertyEvidence: propertyEvidenceCols(
      filteredOptions,
      actionMethods?.openMenu,
    ),
    ncicSearchRequests: ncicSearchRequestsCols(openMenu),
    towingCompanies: towingCompaniesCols(openMenu),
    towingRecords: towingRecords(openMenu),
    internalAffairs: internalAffairsCols(openMenu, actionMethods?.iaActions),
    peopleInvolved: InternalAffairsPeopleInvolvedCols(openMenu),
    relatedRecordsIA: InternalAffairsRelatedRecordsCols(
      actionMethods?.onUnlink,
    ),
    auditTrail: InternalAffairsAuditTrailsCols(),
    inspectionFeesCols: inspectionFeesCols(onEdit),
    inspectionViolationsCols: inspectionViolationsCols(actionMethods),
    buildingInspectionListCols: buildingInspectionRecordsCols(onDelete, onEdit),
    inspectionVisitNotes: inspectionVisitNotes(onDelete, onEdit),
    domesticIncidentReports: domesticIncidentReportCols(actionMethods),
    arrestsTool: arrestsToolCols(
      navigate,
      filteredOptions,
      actionMethods?.onPrint,
      actionMethods?.isPrinting,
      actionMethods?.printingArresteeId,
      actionMethods?.onView,
      actionMethods?.onEdit,
      actionMethods?.onExport,
      isReadOnly,
    ),
    personCfs: personCfsCols(),
    personArrests: personArrestCols(),
    personCases: personCaseCols(),
    personWarrants: personWarrantCols(),
    personTasks: personTaskCols(),
    personDir: personDirCols(),
    sexOffender: sexOffenderCols(openMenu),
  };

  return useMemo(
    () => createColumns(columnSettings, gridSpecificMaps[type]),
    [
      columnSettings,
      type,
      navigate,
      onDelete,
      openMenu,
      onView,
      onEdit,
      onPrint,
      isReadOnly,
      hasViewRole,
      filteredOptions,
      agencyPropertyTypes,
      isNameFilterActive,
      actionMethods,
      cfsDisplayOption,
      onInvolvementUpdate,
    ],
  );
};
